import React from 'react';
import { Users, Edit, Search, Calendar, DollarSign, MapPin, Bell } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { EditOpportunityModal } from './EditOpportunityModal';
import { AlertFeedback } from './AlertFeedback';
import { createAlertsFromOpportunity } from '../services/alerts';
import { useInView } from 'react-intersection-observer';

interface Opportunity {
  id: string;
  origin: string;
  destination: string;
  departure_dates: string[];
  return_dates: string[];
  value: number;
  miles: number;
  fees_included: boolean;
  loyalty_program: string;
  sent: boolean;
  created_at: string;
}

export function OpportunitiesList() {
  const [opportunities, setOpportunities] = React.useState<Opportunity[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [editingOpportunity, setEditingOpportunity] = React.useState<Opportunity | null>(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(true);
  const [isFetchingMore, setIsFetchingMore] = React.useState(false);
  const [isGeneratingAlerts, setIsGeneratingAlerts] = React.useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = React.useState<string | null>(null);
  const { ref: loadMoreRef, inView } = useInView();

  const handleGenerateAlerts = async (opportunity: Opportunity) => {
    try {
      setIsGeneratingAlerts(opportunity.id);
      
      // Generate alerts and get result
      const alerts = await createAlertsFromOpportunity(
        opportunity.origin,
        opportunity.destination,
        opportunity.departure_dates.map(d => new Date(d)),
        opportunity.return_dates.map(d => new Date(d)),
        opportunity.value,
        opportunity.fees_included
      );

      // Show feedback based on result
      if (alerts && alerts.length > 0) {
        setFeedbackMessage(`Alertas gerados com sucesso! ${alerts.length} novos alertas foram criados.`);
      } else {
        setFeedbackMessage('Nenhum novo alerta foi gerado para as oportunidades selecionadas.');
      }

      // Update opportunity status
      const { error: updateError } = await supabase
        .from('opportunities')
        .update({ sent: true })
        .eq('id', opportunity.id);

      if (updateError) throw updateError;

      // Reload opportunities
      loadOpportunities(0, searchTerm);
      setPage(0);
    } catch (err) {
      console.error('Error generating alerts:', err);
      setFeedbackMessage('Erro ao gerar alertas. Por favor, tente novamente.');
    } finally {
      setIsGeneratingAlerts(null);
    }
  };

  const loadOpportunities = React.useCallback(async (pageNumber: number, search: string = '') => {
    try {
      setIsFetchingMore(true);
      const from = pageNumber * 50;
      const to = from + 49;

      let query = supabase
        .from('opportunities')
        .select('*', { count: 'exact' });

      if (search) {
        query = query.or(`origin.ilike.%${search}%,destination.ilike.%${search}%`);
      }

      const { data, error: fetchError, count } = await query
        .order('created_at', { ascending: false })
        .range(from, to);

      if (fetchError) throw fetchError;

      if (pageNumber === 0) {
        setOpportunities(data || []);
      } else {
        setOpportunities(prev => [...prev, ...(data || [])]);
      }

      setHasMore(count ? from + 50 < count : false);
    } catch (err) {
      console.error('Error loading opportunities:', err);
      setError('Erro ao carregar oportunidades');
    } finally {
      setIsFetchingMore(false);
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadOpportunities(0, searchTerm);
    setPage(0);
  }, [loadOpportunities, searchTerm]);

  React.useEffect(() => {
    if (inView && hasMore && !isFetchingMore && !isLoading) {
      setPage(prev => {
        const nextPage = prev + 1;
        loadOpportunities(nextPage, searchTerm);
        return nextPage;
      });
    }
  }, [inView, hasMore, isFetchingMore, isLoading, loadOpportunities, searchTerm]);

  if (isLoading) {
    return <div className="text-center py-4">Carregando...</div>;
  }

  if (error) {
    return (
      <div className="p-4 bg-red-100 text-red-700 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-3 mb-6">
        <Users className="text-blue-600" size={24} />
        <h2 className="text-xl font-semibold text-gray-900">
          Oportunidades Cadastradas ({opportunities.length})
        </h2>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Buscar por origem ou destino..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="space-y-4">
        {opportunities.map((opportunity) => (
          <div
            key={opportunity.id}
            className="bg-gray-50 rounded-lg p-4 space-y-4"
          >
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <MapPin size={18} className="text-gray-400" />
                  <div>
                    <span className="font-medium text-gray-900">{opportunity.origin}</span>
                    <span className="mx-2 text-gray-400">→</span>
                    <span className="font-medium text-gray-900">{opportunity.destination}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Calendar size={18} className="text-gray-400" />
                  <div className="text-sm text-gray-600">
                    <div>
                      <span className="font-medium">Ida:</span>{' '}
                      {opportunity.departure_dates.map(date => 
                        new Date(date).toLocaleDateString()
                      ).join(', ')}
                    </div>
                    <div>
                      <span className="font-medium">Volta:</span>{' '}
                      {opportunity.return_dates.map(date => 
                        new Date(date).toLocaleDateString()
                      ).join(', ')}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <DollarSign size={18} className="text-gray-400" />
                  <span className="font-medium text-gray-900">
                    R$ {opportunity.value.toFixed(2)}
                  </span>
                  {opportunity.fees_included && (
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                      Taxas inclusas
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-2">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  opportunity.sent
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {opportunity.sent ? 'Enviado' : 'Pendente'}
                  </span>
                  <button
                    onClick={() => setEditingOpportunity(opportunity)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Editar oportunidade"
                  >
                    <Edit size={18} />
                  </button>
                  {!opportunity.sent && (
                    <button
                      onClick={() => handleGenerateAlerts(opportunity)}
                      disabled={isGeneratingAlerts === opportunity.id}
                      className={`p-2 rounded-lg transition-colors ${
                        isGeneratingAlerts === opportunity.id
                          ? 'opacity-50 cursor-not-allowed'
                          : 'text-green-600 hover:bg-green-50 flex items-center gap-2'
                      }`}
                      title="Gerar alertas"
                    >
                      <Bell size={18} />
                      <span>Gerar alertas</span>
                      {isGeneratingAlerts === opportunity.id && (
                        <span className="sr-only">Gerando alertas...</span>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Infinite scroll trigger */}
        {hasMore && (
          <div ref={loadMoreRef} className="py-4 text-center">
            {isFetchingMore ? 'Carregando mais...' : ''}
          </div>
        )}

        {!isLoading && opportunities.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Nenhuma oportunidade encontrada
          </div>
        )}
      </div>

      {editingOpportunity && (
        <EditOpportunityModal
          opportunity={editingOpportunity}
          onClose={() => setEditingOpportunity(null)}
          onSuccess={() => {
            loadOpportunities(0);
            setPage(0);
          }}
        />
      )}
      
      {feedbackMessage && (
        <AlertFeedback
          message={feedbackMessage}
          onClose={() => setFeedbackMessage(null)}
        />
      )}
    </>
  );
}